import { useState, useEffect, createContext, useContext, useCallback, type ReactNode } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
  action?: ToastAction;
  durationMs: number;
}

interface ToastOptions {
  action?: ToastAction;
  /** v0.57.2 — custom duration (default 3000ms, undo toasts używają 10000ms). */
  durationMs?: number;
}

interface ToastContextType {
  toast: (message: string, type?: ToastItem['type'], options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const toast = useCallback((
    message: string,
    type: ToastItem['type'] = 'info',
    options: ToastOptions = {},
  ) => {
    const id = nextId++;
    setToasts((prev) => {
      const next = [...prev, {
        id,
        message,
        type,
        action: options.action,
        durationMs: options.durationMs ?? 3000,
      }];
      return next.length > 5 ? next.slice(-5) : next;
    });
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed right-4 top-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <ToastItem key={t.id} item={t} onDone={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ item, onDone }: { item: ToastItem; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, item.durationMs);
    return () => clearTimeout(timer);
  }, [onDone, item.durationMs]);

  // F1.5 — tokeny blueprint zamiast hardkodów
  const colors = {
    success: 'text-white',
    error: 'text-white',
    info: 'text-white',
  };
  const bgStyles: Record<string, React.CSSProperties> = {
    success: { backgroundColor: 'var(--ok)' },
    error: { backgroundColor: 'var(--err)' },
    info: { backgroundColor: 'var(--ink)' },
  };

  return (
    <div
      className={`animate-slide-in flex items-center gap-3 rounded px-4 py-2.5 text-sm shadow-lg ${colors[item.type]}`}
      style={bgStyles[item.type]}
    >
      <span
        className="flex-1 cursor-pointer"
        onClick={item.action ? undefined : onDone}
      >
        {item.message}
      </span>
      {item.action && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            item.action!.onClick();
            onDone();
          }}
          className="rounded-md bg-white/20 px-2 py-1 text-xs font-semibold uppercase tracking-wide hover:bg-white/30"
        >
          {item.action.label}
        </button>
      )}
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
